import React from "react";
import { Award, BadgeCheck, ExternalLink } from "lucide-react";

export default function Certifications({ items = [] }) {
    if (items.length === 0) return null;
    return (
        <section
            id="certifications"
            data-testid="certifications-section"
            className="relative py-24 md:py-32 border-t border-white/5"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
                    <div>
                        <div className="mono-label mb-4 flex items-center gap-2">
                            <Award size={11} className="text-brand" /> Credentials
                        </div>
                        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95] tracking-tighter">
                            Certified &<br />
                            <span className="text-brand">accountable</span>.
                        </h2>
                    </div>
                    <p className="text-ink-200 max-w-md">
                        Accredited certifications and verified credentials demonstrating commitment to engineering excellence and continuous learning.
                    </p>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                    {items.map((c) => {
                        const card = (
                            <div
                                className="group p-6 rounded-2xl border border-white/5 bg-ink-800/40 hover:border-brand/40 hover:bg-ink-800 hover:-translate-y-1 transition-all relative overflow-hidden"
                                data-testid={`cert-${(c.name + "-" + c.issuer).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            >
                                <div className="flex items-start gap-4">
                                    <div className="relative w-16 h-16 rounded-xl bg-white/95 flex items-center justify-center overflow-hidden flex-shrink-0">
                                        {c.badge_url ? (
                                            <img
                                                src={c.badge_url}
                                                alt={c.issuer}
                                                loading="lazy"
                                                decoding="async"
                                                className="w-full h-full object-contain p-2"
                                                onError={(e) => {
                                                    e.currentTarget.style.display = "none";
                                                    e.currentTarget.parentElement.innerHTML = `<svg class='w-7 h-7 text-black' viewBox='0 0 24 24' fill='currentColor'><path d='M12 2 9 7l-5 1 4 4-1 6 5-3 5 3-1-6 4-4-5-1Z'/></svg>`;
                                                }}
                                            />
                                        ) : (
                                            <Award size={26} className="text-black" />
                                        )}
                                        <BadgeCheck size={16} className="absolute -bottom-1 -right-1 text-emerald-400 fill-ink-900" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-start justify-between gap-3">
                                            <h3 className="font-display text-xl text-white tracking-tight leading-tight">{c.name}</h3>
                                            {c.credential_url && (
                                                <ExternalLink size={14} className="text-ink-400 group-hover:text-brand transition-colors flex-shrink-0 mt-1" />
                                            )}
                                        </div>
                                        <div className="mt-1 text-sm text-ink-100">{c.issuer}</div>
                                        <div className="mt-3 flex flex-wrap items-center gap-2">
                                            {c.year && (
                                                <span className="mono-label px-2 py-0.5 rounded bg-white/5 border border-white/5">
                                                    {c.year}
                                                </span>
                                            )}
                                            {c.credential_id && (
                                                <span className="mono-label px-2 py-0.5 rounded bg-white/5 border border-white/5 truncate max-w-[200px] text-xs">
                                                    ID: {c.credential_id}
                                                </span>
                                            )}
                                        </div>
                                        {c.description && (
                                            <p className="mt-4 text-sm text-ink-200 leading-relaxed">{c.description}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                        if (c.credential_url)
                            return (
                                <a key={c.id} href={c.credential_url} target="_blank" rel="noreferrer">
                                    {card}
                                </a>
                            );
                        return <div key={c.id}>{card}</div>;
                    })}
                </div>
            </div>
        </section>
    );
}
